import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AutoComplete,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Spin,
  message
} from "antd";
import dayjs from "dayjs";
import ReportButton from "@controls/report-button";
import { REPORT_VID } from "@components/service/reports/report-types";
import { fetchSprTov } from "@components/health/clients/health-clients-api";
import {
  getSetting,
  getWorkplaceNumber,
  loadRuntimeSettings
} from "@core/runtime-settings";
import { fetchSprPredList } from "../../sprs/pred/spr-pred-api";
import {
  createDocPlat,
  fetchDocPlat,
  fetchDocPlatLookups,
  fetchDocPlatNextDocnum,
  saveDocPlat
} from "./doc-plat-api";
import {
  calcSymaExp,
  getLawFlag,
  normalizeListRow,
  normalizeLookups,
  pick,
  toNumber
} from "./doc-plat-utils";
import calendarSrc from "./assets/calendar.svg";
import chevronSrc from "./assets/chevron-down.svg";
import xSrc from "./assets/x.svg";
import "./doc-plats.css";

const formatMoneyInput = (value) => {
  if (value === "" || value == null) return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return `${n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
};

const parseMoneyInput = (value) =>
  String(value ?? "")
    .replace(/\s/g, "")
    .replace("₽", "")
    .replace(",", ".");

const formatPercentInput = (value) => {
  if (value === "" || value == null) return "";
  return `${value}%`;
};

const parsePercentInput = (value) => String(value ?? "").replace("%", "").replace(",", ".");

const CalendarIcon = () => (
  <span className="doc-plat-drawer__icon">
    <img src={calendarSrc} alt="" />
  </span>
);

const ChevronIcon = () => (
  <span className="doc-plat-drawer__icon">
    <img src={chevronSrc} alt="" />
  </span>
);

const normalizeTovOption = (row) => {
  const idTov = toNumber(pick(row, "idTov", "IdTov"));
  const name = pick(row, "name", "Name") ?? "";
  const zena = toNumber(pick(row, "zenaExp", "ZenaExp", "zena", "Zena"));
  return {
    value: idTov,
    label: name,
    zenaExp: zena ?? 0
  };
};

const predCollator = new Intl.Collator("ru", { sensitivity: "base", numeric: true });
const sortPredOptions = (options) =>
  [...options].sort((a, b) => predCollator.compare(String(a.label ?? ""), String(b.label ?? "")));

const DRAWER_WIDTH_KEY = "planOnline.docPlat.cardDrawer.width.v2";
const MIN_WIDTH = 420;
const MAX_WIDTH = 720;
const DEFAULT_WIDTH = 480;
const SCREEN_MARGIN = 100;

const maxAllowedWidth = () => {
  if (typeof window === "undefined") return MAX_WIDTH;
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, window.innerWidth - SCREEN_MARGIN));
};

const clampWidth = (value) => {
  const max = maxAllowedWidth();
  const n = Number(value);
  if (!Number.isFinite(n)) return Math.min(DEFAULT_WIDTH, max);
  return Math.min(max, Math.max(MIN_WIDTH, Math.round(n)));
};

const loadWidth = () => {
  try {
    return clampWidth(localStorage.getItem(DRAWER_WIDTH_KEY) ?? DEFAULT_WIDTH);
  } catch {
    return DEFAULT_WIDTH;
  }
};

const saveWidth = (value) => {
  try {
    localStorage.setItem(DRAWER_WIDTH_KEY, String(clampWidth(value)));
  } catch {
    /* ignore */
  }
};

/**
 * Drawer карточки «Платёж». Delphi: TFrmEJurnPlat.
 */
const DocPlatEditDrawer = ({ open, idReg, isNew, law, onCancel, onDone }) => {
  const [form] = Form.useForm();
  const [size, setSize] = useState(loadWidth);
  const [lookups, setLookups] = useState({
    vids: [],
    kassas: [],
    sklads: [],
    tovs: [],
    txtOsns: [],
    defaultIdKassa: 0
  });
  const [predOptions, setPredOptions] = useState([]);
  const [predLoading, setPredLoading] = useState(false);
  const [tovOptions, setTovOptions] = useState([{ value: 0, label: "—" }]);
  const [tovLoading, setTovLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [runtimeSettings, setRuntimeSettings] = useState(null);
  const [markReadonly, setMarkReadonly] = useState({ mark: "", hsReqId: "", hsReqTimestamp: "" });
  const loadReqRef = useRef(0);
  const tovSearchRef = useRef(0);
  const tovTimerRef = useRef(0);
  const predSearchRef = useRef(0);
  const predTimerRef = useRef(0);

  const canEditSyma = getLawFlag(law, 74) === 1;
  const canEditDate = getLawFlag(law, 77) === 1;

  const syma = Form.useWatch("syma", form);
  const qty = Form.useWatch("qty", form);
  const prcDiscount = Form.useWatch("prcDiscount", form);
  const vidWatch = Form.useWatch("vid", form);
  const docnumWatch = Form.useWatch("docnum", form);
  const idTovWatch = Form.useWatch("idTov", form);
  const symaExp = useMemo(() => calcSymaExp(syma, qty, prcDiscount), [syma, qty, prcDiscount]);
  const isIncome = (Number(vidWatch) || 1) > 0;
  const hasTov = Number(idTovWatch) > 0;
  const hasSklads = (lookups.sklads || []).some((o) => Number(o.value) > 0);

  useEffect(() => {
    if (!open) return undefined;
    const onWinResize = () => setSize((prev) => clampWidth(prev));
    onWinResize();
    window.addEventListener("resize", onWinResize);
    return () => window.removeEventListener("resize", onWinResize);
  }, [open]);

  const onResize = useCallback((next) => {
    setSize(clampWidth(next));
  }, []);

  const onResizeEnd = useCallback(
    (finalSize) => {
      const next = clampWidth(typeof finalSize === "number" ? finalSize : size);
      setSize(next);
      saveWidth(next);
    },
    [size]
  );

  const loadTovOptions = useCallback((q) => {
    clearTimeout(tovTimerRef.current);
    tovTimerRef.current = setTimeout(() => {
      const requestId = ++tovSearchRef.current;
      setTovLoading(true);
      fetchSprTov(q)
        .then((data) => {
          if (requestId !== tovSearchRef.current) return;
          const opts = (Array.isArray(data) ? data : [])
            .map(normalizeTovOption)
            .filter((o) => o.value);
          const selectedId = Number(form.getFieldValue("idTov")) || 0;
          setTovOptions((prev) => {
            // Сохраняем выбранный текущий товар (value>0), если его нет в ответе поиска.
            const selected = prev.filter(
              (p) => p.value === selectedId && p.value > 0 && !opts.some((o) => o.value === p.value)
            );
            return [{ value: 0, label: "—" }, ...selected, ...opts];
          });
        })
        .catch(() => {
          if (requestId !== tovSearchRef.current) return;
        })
        .finally(() => {
          if (requestId === tovSearchRef.current) setTovLoading(false);
        });
    }, 300);
  }, [form]);

  const loadPredOptions = useCallback((q) => {
    const query = typeof q === "string" ? q.trim() : "";
    clearTimeout(predTimerRef.current);
    const selectedId = Number(form.getFieldValue("idClient")) || 0;
    if (!query) {
      predSearchRef.current += 1;
      setPredLoading(false);
      setPredOptions((prev) => prev.filter((p) => p.value === selectedId));
      return;
    }
    predTimerRef.current = setTimeout(() => {
      const requestId = ++predSearchRef.current;
      setPredLoading(true);
      fetchSprPredList({ q: query, take: 20 })
        .then((preds) => {
          if (requestId !== predSearchRef.current) return;
          const predItems = Array.isArray(preds)
            ? preds
            : pick(preds, "items", "Items") || [];
          const predOpts = predItems
            .map((p) => {
              const value = toNumber(pick(p, "idPred", "IdPred"));
              if (!value) return null;
              const name = pick(p, "name", "Name") || "";
              const phone = pick(p, "phone", "Phone") || "";
              return {
                value,
                label: phone ? `${name} · ${phone}` : name
              };
            })
            .filter(Boolean);
          setPredOptions((prev) => {
            // Сохраняем только выбранного контрагента, если его нет в ответе поиска.
            const selected = prev.filter(
              (p) => p.value === selectedId && !predOpts.some((o) => o.value === p.value)
            );
            return sortPredOptions([...selected, ...predOpts]);
          });
        })
        .catch(() => {
          if (requestId !== predSearchRef.current) return;
        })
        .finally(() => {
          if (requestId === predSearchRef.current) setPredLoading(false);
        });
    }, 300);
  }, [form]);

  useEffect(() => {
    if (!open) return undefined;
    const requestId = ++loadReqRef.current;
    setLoading(true);
    setPredOptions([]);
    setTovOptions([{ value: 0, label: "—" }]);
    setPredLoading(false);
    clearTimeout(predTimerRef.current);
    clearTimeout(tovTimerRef.current);

    const cardPromise =
      isNew || idReg == null ? Promise.resolve(null) : fetchDocPlat(idReg);

    Promise.all([
      fetchDocPlatLookups(),
      cardPromise,
      loadRuntimeSettings().catch(() => ({}))
    ])
      .then(([lu, row, settings]) => {
        if (requestId !== loadReqRef.current) return;
        setRuntimeSettings(settings);
        const lookupsNorm = normalizeLookups(lu);
        setLookups(lookupsNorm);

        if (isNew) {
          setMarkReadonly({ mark: "", hsReqId: "", hsReqTimestamp: "" });
          form.setFieldsValue({
            docnum: undefined,
            fdate: dayjs(),
            idKassa: lookupsNorm.defaultIdKassa || undefined,
            vid: 1,
            idClient: undefined,
            idTov: 0,
            syma: 0,
            qty: 1,
            prcDiscount: 0,
            idSkl: getWorkplaceNumber("idSkl", 0),
            txtOsn: "",
            idText: undefined,
            komment: "",
            dateUse: null
          });
        } else if (row) {
          const n = normalizeListRow(row);
          setMarkReadonly({
            mark: n.mark || "",
            hsReqId: n.hsReqId || "",
            hsReqTimestamp: n.hsReqTimestamp || ""
          });
          if (n.idTov) {
            setTovOptions([
              { value: 0, label: "—" },
              { value: n.idTov, label: n.tovName || `#${n.idTov}` }
            ]);
          }
          if (n.idClient) {
            setPredOptions([{ value: n.idClient, label: n.predName || String(n.idClient) }]);
          }
          form.setFieldsValue({
            docnum: n.docnum || undefined,
            fdate: n.fdate ? dayjs(n.fdate) : null,
            idKassa: n.idKassa || undefined,
            vid: n.vid,
            idClient: n.idClient || undefined,
            idTov: n.idTov || 0,
            syma: n.syma,
            qty: n.qty,
            prcDiscount: n.prcDiscount,
            idSkl: n.idSkl || 0,
            txtOsn: n.txtOsn || "",
            idText: n.idText || undefined,
            komment: n.komment || "",
            dateUse: n.dateUse ? dayjs(n.dateUse) : null
          });
        }
      })
      .catch((e) => {
        if (requestId !== loadReqRef.current) return;
        message.error(e.message || "Ошибка загрузки");
      })
      .finally(() => {
        if (requestId === loadReqRef.current) setLoading(false);
      });

    loadTovOptions("");

    return () => {
      loadReqRef.current += 1;
      clearTimeout(predTimerRef.current);
      clearTimeout(tovTimerRef.current);
    };
  }, [open, idReg, isNew, form, loadTovOptions]);

  const setVidSign = (income) => {
    const current = Number(form.getFieldValue("vid")) || 1;
    const abs = Math.abs(current) || 1;
    form.setFieldsValue({ vid: income ? abs : -abs });
  };

  const onTxtOsnPick = (label, option) => {
    const idText = option?.id ?? (lookups.txtOsns || []).find((x) => x.label === label)?.value;
    form.setFieldsValue({
      idText: idText || 0,
      txtOsn: label
    });
  };

  const onNextDocnum = async () => {
    const idKassa = form.getFieldValue("idKassa");
    const vid = form.getFieldValue("vid") ?? 1;
    const fdate = form.getFieldValue("fdate");
    if (!idKassa) {
      message.warning("Укажите кассу");
      return;
    }
    try {
      const res = await fetchDocPlatNextDocnum({
        idKassa,
        vid,
        fdate: fdate ? fdate.format("YYYY-MM-DD") : undefined
      });
      form.setFieldsValue({
        docnum: toNumber(pick(res, "docnum", "Docnum")) ?? undefined
      });
    } catch (e) {
      message.error(e.message || "Не удалось получить номер");
    }
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const body = {
        docnum: values.docnum || 0,
        fdate: values.fdate ? values.fdate.startOf("day").toISOString() : null,
        idKassa: values.idKassa || 0,
        vid: values.vid ?? 1,
        idClient: values.idClient || 0,
        idTov: values.idTov || 0,
        syma: values.syma ?? 0,
        qty: values.qty ?? 1,
        prcDiscount: values.prcDiscount ?? 0,
        idSkl: values.idSkl || 0,
        txtOsn: values.txtOsn || "",
        idText: values.idText || 0,
        komment: values.komment || "",
        dateUse: values.dateUse ? values.dateUse.startOf("day").toISOString() : null
      };
      const saved = isNew
        ? await createDocPlat(body)
        : await saveDocPlat({ ...body, idReg });
      message.success(isNew ? "Платёж создан" : "Платёж сохранён");
      onDone?.(saved);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  };

  const title = docnumWatch
    ? `Платёж #${docnumWatch}`
    : isNew
      ? "Платёж"
      : `Платёж${idReg != null ? ` #${idReg}` : ""}`;
  const requirePurpose =
    Number(getSetting(runtimeSettings, "PLAT_OSN_NECESSARY", 0)) === 1;
  const requireService = Number(getSetting(runtimeSettings, "PLAT_USL_NEED", 0)) === 1;
  const purposeOptions = (lookups.txtOsns || []).map((x) => ({ value: x.label, id: x.value }));

  return (
    <Drawer
      placement="right"
      title={title}
      size={size}
      maxSize={maxAllowedWidth()}
      open={open}
      onClose={onCancel}
      destroyOnClose
      rootClassName="doc-plat-drawer"
      closable={false}
      extra={
        <div className="doc-plat-drawer__extra">
          <div className="doc-plat-drawer__print">
            <ReportButton
              vid={REPORT_VID.PAYMENTS}
              id={idReg}
              compact
              disabled={isNew || idReg == null}
            />
          </div>
          <button
            type="button"
            className="doc-plat-drawer__close-btn"
            aria-label="Закрыть"
            title="Закрыть"
            disabled={submitting}
            onClick={onCancel}
          >
            <span className="doc-plat-drawer__close">
              <img src={xSrc} alt="" />
            </span>
          </button>
        </div>
      }
      resizable={{
        onResize,
        onResizeEnd
      }}
      footer={
        <div className="doc-plat-drawer__footer">
          <Button
            type="primary"
            className="doc-plat-drawer__save"
            loading={submitting}
            disabled={loading}
            onClick={onSave}
          >
            ОК
          </Button>
          <button
            type="button"
            className="doc-plat-drawer__cancel"
            disabled={submitting}
            onClick={onCancel}
          >
            Отмена
          </button>
        </div>
      }
    >
      {open ? (
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            disabled={loading || submitting}
            className="doc-plat-drawer__form"
            requiredMark={(label, { required }) =>
              required ? (
                <>
                  <span className="doc-plat-drawer__req">*</span>
                  {label}
                </>
              ) : (
                label
              )
            }
          >
            <div className="doc-plat-drawer__row">
              <Form.Item
                name="docnum"
                extra={
                  isNew ? (
                    <button
                      type="button"
                      className="doc-plat-drawer__link"
                      disabled={loading || submitting}
                      onClick={onNextDocnum}
                    >
                      Новый №
                    </button>
                  ) : null
                }
              >
                <InputNumber min={0} controls={false} placeholder="№ документа" />
              </Form.Item>
              <Form.Item
                name="fdate"
                rules={[{ required: true, message: "Укажите дату" }]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  placeholder="Дата"
                  disabled={!canEditDate}
                  suffixIcon={<CalendarIcon />}
                />
              </Form.Item>
            </div>

            <Form.Item className="doc-plat-drawer__type">
              <div className="doc-plat-drawer__toggle">
                <button
                  type="button"
                  className={`doc-plat-drawer__toggle-btn${isIncome ? " is-active" : ""}`}
                  disabled={loading || submitting}
                  onClick={() => setVidSign(true)}
                >
                  Приходный
                </button>
                <button
                  type="button"
                  className={`doc-plat-drawer__toggle-btn${!isIncome ? " is-active" : ""}`}
                  disabled={loading || submitting}
                  onClick={() => setVidSign(false)}
                >
                  Расходный
                </button>
              </div>
            </Form.Item>
            <Form.Item name="vid" hidden>
              <InputNumber />
            </Form.Item>

            <Form.Item
              name="idKassa"
              label="Касса"
              rules={[{ required: true, message: "Укажите кассу" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={lookups.kassas}
                disabled={!isNew}
                suffixIcon={<ChevronIcon />}
              />
            </Form.Item>

            <Form.Item
              name="idTov"
              label="Товар / услуга"
              rules={
                requireService
                  ? [
                      {
                        validator: (_, value) =>
                          Number(value) > 0
                            ? Promise.resolve()
                            : Promise.reject(new Error("Укажите товар или услугу"))
                      }
                    ]
                  : undefined
              }
            >
              <Select
                showSearch
                filterOption={false}
                options={tovOptions}
                loading={tovLoading}
                suffixIcon={<ChevronIcon />}
                onSearch={(q) => loadTovOptions(q)}
                onDropdownVisibleChange={(visible) => {
                  if (visible) loadTovOptions("");
                }}
                onChange={(value, option) => {
                  const next = {
                    ...(Number(value) > 0 ? {} : { idSkl: 0, dateUse: null })
                  };
                  if (isNew && option?.zenaExp != null && canEditSyma) {
                    next.syma = option.zenaExp;
                  }
                  if (Object.keys(next).length) form.setFieldsValue(next);
                }}
              />
            </Form.Item>

            <div className="doc-plat-drawer__money">
              <Form.Item name="syma" label="Цена">
                <InputNumber
                  min={0}
                  step={0.01}
                  controls={false}
                  disabled={!canEditSyma}
                  formatter={formatMoneyInput}
                  parser={parseMoneyInput}
                />
              </Form.Item>
              <Form.Item name="qty" label="Кол.">
                <InputNumber min={0} step={0.01} controls={false} disabled={!canEditSyma} />
              </Form.Item>
              <Form.Item name="prcDiscount" label="Скидка %">
                <InputNumber
                  min={0}
                  max={100}
                  step={0.01}
                  controls={false}
                  disabled={!canEditSyma}
                  formatter={formatPercentInput}
                  parser={parsePercentInput}
                />
              </Form.Item>
              <Form.Item label="Сумма">
                <InputNumber value={symaExp} disabled controls={false} formatter={formatMoneyInput} />
              </Form.Item>
            </div>

            <Form.Item
              name="txtOsn"
              label="Назначение платежа"
              rules={
                requirePurpose
                  ? [{ required: true, whitespace: true, message: "Укажите назначение платежа" }]
                  : undefined
              }
            >
              <AutoComplete
                options={purposeOptions}
                suffixIcon={<ChevronIcon />}
                filterOption={(input, option) =>
                  (option?.value || "").toLowerCase().includes((input || "").toLowerCase())
                }
                onSelect={onTxtOsnPick}
              />
            </Form.Item>
            <Form.Item name="idText" hidden>
              <InputNumber />
            </Form.Item>

            <Form.Item name="idClient" label="Контрагент / Клиент">
              <Select
                showSearch
                filterOption={false}
                options={predOptions}
                allowClear
                loading={predLoading}
                suffixIcon={<ChevronIcon />}
                placeholder="Начните вводить для поиска"
                notFoundContent={predLoading ? "Поиск..." : "Введите текст для поиска"}
                onSearch={loadPredOptions}
              />
            </Form.Item>

            <Form.Item name="komment" label="Комментарий">
              <Input placeholder="Комментарий" />
            </Form.Item>

            <Form.Item name="dateUse" label="Дата использования">
              <DatePicker
                format="DD.MM.YYYY"
                disabled={!hasTov}
                suffixIcon={<CalendarIcon />}
              />
            </Form.Item>

            <Form.Item name="idSkl" label="Склад">
              <Select
                options={lookups.sklads}
                disabled={!hasTov || !hasSklads}
                suffixIcon={<ChevronIcon />}
              />
            </Form.Item>

            {!isNew && (markReadonly.mark || markReadonly.hsReqId) ? (
              <div className="doc-plat-drawer__row doc-plat-drawer__row--3">
                <Form.Item label="Маркировка">
                  <Input value={markReadonly.mark} disabled />
                </Form.Item>
                <Form.Item label="ЧЗ #">
                  <Input value={markReadonly.hsReqId} disabled />
                </Form.Item>
                <Form.Item label="ЧЗ дата">
                  <Input value={markReadonly.hsReqTimestamp} disabled />
                </Form.Item>
              </div>
            ) : null}
          </Form>
        </Spin>
      ) : null}
    </Drawer>
  );
};

export default DocPlatEditDrawer;
